import { useEffect, useMemo, useRef, useState } from "react";

import { useFormikContext } from "formik";
import { debounce } from "lodash-es";
import PropTypes from "prop-types";

import { Info } from "components/elements";

import { TEXTS } from "./constants";

const GET_ERROR_DEFAULT = () => undefined;

const GET_VALIDATION_ERROR_DEFAULT = () => undefined;

const MUTATIONS_DEFAULT = [];

const PENDING_DEBOUNCE_TIME = 250;

function getLastMutation(mutations) {
  return mutations
    .toSorted((a, b) => (a.submittedAt || 0) - (b.submittedAt || 0))
    .slice(-1)[0];
}

/**
 * @typedef {object} FormStatusProps
 * @property {Array} [draftMutations]
 *  An array of all mutations that create, edit or delete the draft. The
 *  mutations should originate from useMutation() or have a matching interface.
 * @property {Array} [submitMutations]
 *  An array of all mutations that take part in the form submission. The
 *  mutations should originate from useMutation() or have a matching interface.
 * @property {boolean} [withErrorDetails=false]
 *  When `true`, a status message includes an error message from the back end.
 * @property {function} [getSubmitError]
 *  A function that receives a submit error and returns a submit error message
 *  or `undefined` for default handling.
 * @property {function} [getValidationError]
 *  A function that receives form errors and values and returns a validation
 *  error message or `undefined` for default handling.
 *
 * @param {FormStatusProps} props
 */
export function FormStatus({
  draftMutations = MUTATIONS_DEFAULT,
  submitMutations = MUTATIONS_DEFAULT,
  withErrorDetails = false,
  getSubmitError = GET_ERROR_DEFAULT,
  getValidationError = GET_VALIDATION_ERROR_DEFAULT,
}) {
  const { errors, isSubmitting, isValid, submitCount, values } =
    useFormikContext();
  const [formikSubmittedAt, setFormikSubmittedAt] = useState(0);
  const [pending, setPending] = useState(false);
  const [hasChangedSinceSubmit, setHasChangedSinceSubmit] = useState(false);
  const lastSubmitCountRef = useRef(submitCount);

  const lastDraftMutation = useMemo(
    () => getLastMutation(draftMutations),
    [draftMutations]
  );

  const lastSubmitMutation = useMemo(
    () => getLastMutation(submitMutations),
    [submitMutations]
  );

  const stopPending = useMemo(
    () => debounce(() => setPending(false), PENDING_DEBOUNCE_TIME),
    []
  );

  useEffect(() => {
    const isNewSubmit = submitCount > lastSubmitCountRef.current;
    lastSubmitCountRef.current = submitCount;

    if (isNewSubmit) {
      // Formik does not have an equivalent of submittedAt.
      setFormikSubmittedAt(Date.now());
      setHasChangedSinceSubmit(false);
    } else if (submitCount > 0) {
      setHasChangedSinceSubmit(true);
    }
  }, [submitCount, values]);

  // Prevent flickering when the status changes quickly.
  useEffect(() => {
    const nextPending =
      isSubmitting ||
      lastDraftMutation?.isPending ||
      lastSubmitMutation?.isPending;
    if (nextPending) {
      stopPending.cancel();
      setPending(true);
    } else {
      stopPending();
    }
  }, [isSubmitting, lastDraftMutation, lastSubmitMutation, stopPending]);

  const draftTime = Math.max(0, lastDraftMutation?.submittedAt || 0);
  const submitTime = Math.max(
    0,
    formikSubmittedAt,
    lastSubmitMutation?.submittedAt || 0
  );
  const submitted = submitTime !== 0;

  if (pending) {
    return null;
  }

  if (draftTime > submitTime) {
    if (lastDraftMutation.error) {
      let message = TEXTS.DRAFT_ERROR_MESSAGE;
      if (withErrorDetails) {
        message += ` ${lastDraftMutation.error.message || lastDraftMutation.error}`;
      }
      return <Info type="error">{message}</Info>;
    }
    return <Info>{TEXTS.DRAFT_SUCCESS_MESSAGE}</Info>;
  }

  if (!submitted) {
    return null;
  }

  if (!isValid) {
    const message =
      getValidationError(errors, values) || TEXTS.VALIDATION_ERROR_MESSAGE;
    return <Info type="error">{message}</Info>;
  }

  if (!lastSubmitMutation) {
    return <Info>{TEXTS.SUBMIT_SUCCESS_MESSAGE}</Info>;
  }

  if (lastSubmitMutation.isError && !hasChangedSinceSubmit) {
    let defaultMessage = TEXTS.SUBMIT_ERROR_MESSAGE;
    if (withErrorDetails) {
      defaultMessage += ` ${lastSubmitMutation.error.message || lastSubmitMutation.error}`;
    }
    const message = getSubmitError(lastSubmitMutation.error) || defaultMessage;
    return <Info type="error">{message}</Info>;
  }

  if (!lastSubmitMutation.isSuccess) {
    return <Info type="warning">{TEXTS.SUBMIT_RETRY_MESSAGE}</Info>;
  }

  return <Info>{TEXTS.SUBMIT_SUCCESS_MESSAGE}</Info>;
}

FormStatus.propTypes = {
  draftMutations: PropTypes.arrayOf(
    PropTypes.shape({
      error: PropTypes.shape({
        message: PropTypes.string.isRequired,
      }),
      isPending: PropTypes.bool.isRequired,
      isSuccess: PropTypes.bool.isRequired,
      submittedAt: PropTypes.number.isRequired,
    })
  ),
  submitMutations: PropTypes.arrayOf(
    PropTypes.shape({
      error: PropTypes.shape({
        message: PropTypes.string.isRequired,
      }),
      isPending: PropTypes.bool.isRequired,
      isSuccess: PropTypes.bool.isRequired,
      submittedAt: PropTypes.number.isRequired,
    })
  ),
  withErrorDetails: PropTypes.bool,
  getValidationError: PropTypes.func,
};
